import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { flattenTranslationValues } from "../../../../utils/deals/dealFormStepHelpers";
import { humanizeLabel, type TranslationFn } from "../dealFormFields";

export function useDealTranslation() {
  const { t, i18n } = useTranslation("dealModal");

  const reverseTranslationMap = useMemo(() => {
    const enBundle =
      i18n.getResourceBundle("en", "CreateEditOpportunities") || {};
    const dealBundle = i18n.getResourceBundle("en", "dealModal") || {};
    const marketBundle = i18n.getResourceBundle("en", "marketPlaceCards") || {};
    return {
      ...flattenTranslationValues(enBundle),
      ...flattenTranslationValues(dealBundle),
      ...flattenTranslationValues(marketBundle),
    };
  }, [i18n]);

  const tr = useCallback<TranslationFn>(
    (key, fallbackOrOptions = "") => {
      const options =
        typeof fallbackOrOptions === "string"
          ? undefined
          : fallbackOrOptions;
      const fallback =
        typeof fallbackOrOptions === "string"
          ? fallbackOrOptions
          : String(fallbackOrOptions.defaultValue ?? "");

      if (!key) return fallback;

      const direct = String(t(key, options));
      if (direct !== key) return direct;

      const humanizedKey = humanizeLabel(key);
      const mappedKey =
        reverseTranslationMap[key] ??
        reverseTranslationMap[humanizedKey] ??
        reverseTranslationMap[key.trim().toLowerCase()] ??
        reverseTranslationMap[humanizedKey.trim().toLowerCase()];
      if (mappedKey) {
        const mapped = String(t(mappedKey, options));
        if (mapped !== mappedKey) return mapped;

        const createEditMapped = String(
          i18n.t(mappedKey, {
            ns: "CreateEditOpportunities",
            ...(options ?? {}),
          }),
        );
        if (createEditMapped !== mappedKey) return createEditMapped;

        const marketMapped = String(
          i18n.t(mappedKey, {
            ns: "marketPlaceCards",
            ...(options ?? {}),
          }),
        );
        if (marketMapped !== mappedKey) return marketMapped;
      }

      return fallback || key;
    },
    [t, i18n, reverseTranslationMap],
  );

  return { t, tr };
}
